import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import type { TeaGrade } from '@/lib/api/tea-grade-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreateTeaGrade,
    useDeleteTeaGrade,
    usePatchTeaGrade,
    useTeaGradesList,
} from '@/hooks/use-tea-grade-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sorting-warehouse/tea-grades',
)({
    component: TeaGradesPage,
})

interface FormState {
    code: string
    name: string
    sort_order: number
    is_active: boolean
}

const EMPTY_FORM: FormState = {
    code: '',
    name: '',
    sort_order: 0,
    is_active: true,
}

function TeaGradesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('tea_grades.write')

    const { data } = useTeaGradesList()
    // Sort by sort_order ASC để nhìn đúng thứ tự nghiệp vụ (Đặc biệt → Vụn).
    const rows = [...(data?.data ?? [])].sort(
        (a, b) => a.sort_order - b.sort_order,
    )

    const [editing, setEditing] = useState<TeaGrade | null>(null)
    const [open, setOpen] = useState(false)

    const createMut = useCreateTeaGrade()
    const patchMut = usePatchTeaGrade()
    const deleteMut = useDeleteTeaGrade()

    const openCreate = () => {
        setEditing(null)
        setOpen(true)
    }
    const openEdit = (row: TeaGrade) => {
        setEditing(row)
        setOpen(true)
    }
    const handleDelete = (row: TeaGrade) => {
        // Prompt tiếng Việt — key có sẵn với placeholder {name}.
        if (!window.confirm(m.tea_grade_delete_confirm({ name: row.name }))) {
            return
        }
        deleteMut.mutate(row.id)
    }

    return (
        <div className="space-y-4 p-4 md:p-6">
            <ListPageHeader
                title={m.tea_grade_title()}
                subtitle={m.tea_grade_subtitle()}
                utilityActions={
                    canWrite && (
                        <Button size="sm" onClick={openCreate}>
                            <Plus className="size-4 mr-1" />
                            {m.tea_grade_create()}
                        </Button>
                    )
                }
            />

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-16 text-right">
                                {m.tea_grade_sort_order()}
                            </TableHead>
                            <TableHead className="w-32">
                                {m.tea_grade_code()}
                            </TableHead>
                            <TableHead className="min-w-40">
                                {m.tea_grade_name()}
                            </TableHead>
                            <TableHead className="w-28 text-center">
                                {m.tea_grade_is_active()}
                            </TableHead>
                            {canWrite && <TableHead className="w-24" />}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={canWrite ? 5 : 4}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.tea_grade_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell className="text-right tabular-nums">
                                        {row.sort_order}
                                    </TableCell>
                                    <TableCell className="font-mono">
                                        {row.code}
                                    </TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell className="text-center">
                                        <Badge
                                            variant={
                                                row.is_active
                                                    ? 'default'
                                                    : 'secondary'
                                            }
                                        >
                                            {row.is_active ? '✓' : '—'}
                                        </Badge>
                                    </TableCell>
                                    {canWrite && (
                                        <TableCell className="text-right space-x-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => openEdit(row)}
                                            >
                                                <Pencil className="size-4" />
                                            </Button>
                                            {(() => {
                                                const inUseReason =
                                                    deleteBlockReason(row)
                                                // Wrap span (inline-block) để `title` tooltip vẫn hiện khi
                                                // Button ở trạng thái disabled — HTML native quirk: disabled
                                                // button không dispatch mouse events cho tooltip riêng nó.
                                                return (
                                                    <span
                                                        title={
                                                            inUseReason ??
                                                            undefined
                                                        }
                                                        className="inline-block"
                                                    >
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    row,
                                                                )
                                                            }
                                                            disabled={
                                                                deleteMut.isPending ||
                                                                inUseReason !==
                                                                    null
                                                            }
                                                            className={
                                                                inUseReason
                                                                    ? 'pointer-events-none'
                                                                    : undefined
                                                            }
                                                        >
                                                            <Trash2 className="size-4 text-red-500" />
                                                        </Button>
                                                    </span>
                                                )
                                            })()}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {rows.length > 0 && (
                <p className="text-xs text-muted-foreground">
                    {m.tea_grade_total({ count: String(rows.length) })}
                </p>
            )}

            <TeaGradeDialog
                open={open}
                onOpenChange={setOpen}
                initial={editing}
                submitting={createMut.isPending || patchMut.isPending}
                onSubmit={async (values) => {
                    try {
                        if (editing) {
                            await patchMut.mutateAsync({
                                id: editing.id,
                                data: values,
                            })
                        } else {
                            await createMut.mutateAsync(values)
                        }
                        setOpen(false)
                    } catch {
                        // Toast hiện qua onError của mutation — dialog giữ mở để sửa.
                    }
                }}
            />
        </div>
    )
}

function TeaGradeDialog({
    open,
    onOpenChange,
    initial,
    submitting,
    onSubmit,
}: {
    open: boolean
    onOpenChange: (v: boolean) => void
    initial: TeaGrade | null
    submitting: boolean
    onSubmit: (values: FormState) => Promise<void>
}) {
    // Local form state — reset khi initial đổi (edit vs create).
    // Pattern "controlled by initial key" (React docs) — không dùng useEffect
    // vì form ngắn, chỉ 4 field, mỗi lần initial đổi thì sync bằng key check.
    const initialKey = initial?.id ?? 'new'
    const [key, setKey] = useState(initialKey)
    const [form, setForm] = useState<FormState>(() => initialToForm(initial))
    if (key !== initialKey) {
        setKey(initialKey)
        setForm(initialToForm(initial))
    }

    const canSubmit = form.code.trim().length > 0 && form.name.trim().length > 0

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {initial ? m.tea_grade_edit() : m.tea_grade_create()}
                    </DialogTitle>
                </DialogHeader>
                <form
                    className="space-y-3"
                    onSubmit={(e) => {
                        e.preventDefault()
                        if (!canSubmit) return
                        void onSubmit({
                            code: form.code.trim(),
                            name: form.name.trim(),
                            sort_order: form.sort_order,
                            is_active: form.is_active,
                        })
                    }}
                >
                    <div>
                        <Label>{m.tea_grade_code()} *</Label>
                        <Input
                            value={form.code}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    code: e.target.value.toUpperCase(),
                                })
                            }
                            placeholder={m.tea_grade_code_placeholder()}
                            className="font-mono"
                        />
                    </div>
                    <div>
                        <Label>{m.tea_grade_name()} *</Label>
                        <Input
                            value={form.name}
                            onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                            }
                            placeholder={m.tea_grade_name_placeholder()}
                        />
                    </div>
                    <div>
                        <Label>{m.tea_grade_sort_order()}</Label>
                        <Input
                            type="number"
                            value={form.sort_order}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    sort_order: Number(e.target.value) || 0,
                                })
                            }
                            className="w-24"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={form.is_active}
                            onCheckedChange={(v) =>
                                setForm({ ...form, is_active: v })
                            }
                        />
                        <Label>{m.tea_grade_is_active()}</Label>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting || !canSubmit}
                        >
                            {submitting ? '...' : m.common_save()}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

/**
 * Trả về lý do (chuỗi tiếng Việt) khi grade không được xoá vì đang có
 * lô sử dụng. `null` = xoá được. Kết hợp cả 2 nguồn lô phân loại + lấy hương.
 */
function deleteBlockReason(row: TeaGrade): string | null {
    const parts: string[] = []
    if (row.sorting_lots_count > 0) {
        parts.push(
            m.tea_grade_delete_blocked_sorting({
                count: String(row.sorting_lots_count),
            }),
        )
    }
    if (row.scenting_lots_count > 0) {
        parts.push(
            m.tea_grade_delete_blocked_scenting({
                count: String(row.scenting_lots_count),
            }),
        )
    }
    if (parts.length === 0) return null
    return m.tea_grade_delete_blocked_prefix() + ' ' + parts.join(', ')
}

function initialToForm(initial: TeaGrade | null): FormState {
    if (!initial) return { ...EMPTY_FORM }
    return {
        code: initial.code,
        name: initial.name,
        sort_order: initial.sort_order,
        is_active: initial.is_active,
    }
}
